import { memo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useState, MouseEvent, Fragment } from 'react';
import Menu from '@mui/material/Menu';
import { CircularProgress, Grid, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useAppSelector } from "core/store/reduxStore";
import { BadgeCount, DesktopToolBar, FiltersAppliedBadge, TabletMobileToolBar } from "./alerts.styled";
import { 
  ExportBtn, 
  FilterTitle, 
  AlertsToolBarGrid, 
  AlertsToolbarLeftGrid, 
  AlertsToolbarRightGrid, 
  SearchAndStatusBarGrid 
} from "./alerts.styled";

import DateRangePicker from "core/components/global/dateRangePicker/dateRangePicker";
import CasesFilterIcon from "assets/CasesFilterIcon";

import CasesExportIcon from "assets/CasesExportIcon";
import AdvanceSearch from "core/components/global/advanceSearch/advanceSearch";
import { ExportButton } from "./alerts.styled";
import { CustomButtonContainer } from "core/components/global/buttons/button/customButton.styled";
import { Filters2, HomeToolbarInner } from "core/components/global/homeToolbar/homeToolbar.styles";
import { getAlertFilters } from "modules/ac2/services/alertService";
import CustomSort from "core/components/global/customSort/customSort";
import { getOCSFSeverityNameFromNumber } from "core/utils/common";

interface FiltersProps {
  filterData: any;
  setIsFilterModalOpen: (boolean) => void;
  deleteFilter: (arg1: string, arg2: string) => void;
  clearSearch: () => void;
  setFilterData: any;
  
}

function ActiveFilters({filterData, setIsFilterModalOpen, deleteFilter, clearSearch, setFilterData}: FiltersProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();
  
  const handleClickListItem = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <div>
        <CustomButtonContainer
          color="secondary" variant="outlined"
            id="active-filter-menu-toggle"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClickListItem}
            disabled={Object.entries(filterData["filterTerms"]).length > 0 || filterData["searchTerm"] !== "" ? false : true}
          >

            <FilterTitle className="pe-2 d-flex">{t("INCIDENTS_PAGE.FILTERSAPPLIED")}</FilterTitle>
            <FiltersAppliedBadge>
              <BadgeCount>{filterData.totalFilters}</BadgeCount>
            </FiltersAppliedBadge>
        </CustomButtonContainer>

        <Menu
          id="active-filter-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          sx={{padding: "0"}}
        >
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell align="left" variant="head">Column</TableCell>
                  <TableCell align="left" variant="head">Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <>
                  {Object.entries(filterData["filterTerms"]).map(([category, categoryValues], index) => {
                    if (typeof categoryValues === 'string') {
                      // If 'value' is a string, split it and generate TableRow elements
                      return (
                        <Fragment key={`filter-item-${category}`}>
                          {categoryValues.split(',').map((value, valueIndex) => (
                            <TableRow key={`${category}-${valueIndex}`}>
                              <TableCell component="th" scope="row">
                                <CloseIcon onClick={() => deleteFilter(category, value)} />
                              </TableCell>
                              <TableCell align="left">{category.slice(7, -1)}</TableCell>
                              <TableCell align="left">
                                {category.toLowerCase().includes("severity") && !isNaN(Number(value.trim()))
                                  ? getOCSFSeverityNameFromNumber(t, Number(value.trim())).toUpperCase()
                                  : value.trim().toUpperCase()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </Fragment>
                      );
                    } else {
                      // Handle other types of 'value' here
                      return null; // or some other fallback
                    }
                  })}
                  {
                    filterData["searchTerm"] !== "" && <>
                      <TableRow key={`search-item`}>
                        <TableCell component="th" scope="row">
                          <CloseIcon onClick={() => clearSearch()}/>
                        </TableCell>
                        <TableCell align="left">search</TableCell>
                        <TableCell align="left">{filterData["searchTerm"]}</TableCell>
                      </TableRow>
                    </>
                  }
                </>
              </TableBody>
            </Table>
        </Menu>
      </div>
      
      <Filters2>
        <CustomButtonContainer
          variant="outlined"
          data-testid="filter-btn"
          className="all-filters"
          onClick={() => setIsFilterModalOpen(true)}
        >
          <FilterTitle className="pe-2">{t("INCIDENTS_PAGE.ALLFILTERS")}</FilterTitle>
          <CasesFilterIcon />
        </CustomButtonContainer>
        
        <DateRangePicker setFilteredData={setFilterData} />
      </Filters2>
    </>
  );
}

function AlertsToolbar({rowData,headCells, setFilterData, setSearchValue, isFilterModalOpen, setIsFilterModalOpen, setPage, handleExportButton, filterData, deleteFilter, clearSearch,onFilterCreated}: any) {
  const { t } = useTranslation();
  const isExportLoading = useAppSelector((state) => state.alerts.isExportLoading);
  const headerState = useAppSelector((state) => state.header);

  useEffect(() => {
    setIsFilterModalOpen(headerState.openAllFilters);
  }, [headerState.openAllFilters]);

  const nodeRef = useRef(null);
  return (
    <>
      <DesktopToolBar className="">
        <SearchAndStatusBarGrid container>
          <Grid item xs>
            <AdvanceSearch filterByApi={true} rowData={rowData} setFilteredData={setSearchValue}/>
          </Grid>
        </SearchAndStatusBarGrid>

        <AlertsToolBarGrid container className="analystDash-cases-toolbar">
          <AlertsToolbarLeftGrid item xs className="d-flex flex-row pt-2" style={{ gap: '16px' }}>
              <ActiveFilters
                filterData={filterData}
                setIsFilterModalOpen={setIsFilterModalOpen}
                deleteFilter={deleteFilter}
                clearSearch={clearSearch}
                setFilterData={setFilterData}
              />
              <CustomSort 
                table_headers={headCells} 
                setFilterData={setFilterData}
                isFilterModalOpen={isFilterModalOpen || false}
                setIsFilterModalOpen={setIsFilterModalOpen}
                setPage={setPage}
                getFiltersFunction={getAlertFilters}
                onFilterCreated={onFilterCreated}
              />
          </AlertsToolbarLeftGrid>
          <AlertsToolbarRightGrid item xs>
            <div className="d-flex align-items-center mt-2">
              <ExportButton variant="contained" onClick={handleExportButton}>
                <FilterTitle className="pe-2">{t("INCIDENTS_PAGE.EXPORT")}</FilterTitle>
                {isExportLoading ? <CircularProgress size="1rem" sx={{color: "white"}} /> : <CasesExportIcon />}
              </ExportButton>
            </div>
          </AlertsToolbarRightGrid>
        </AlertsToolBarGrid>
      </DesktopToolBar>
      

      <TabletMobileToolBar>
        <div className={headerState.openSearch ? 'component-visible' : 'component-hidden'}>
          <AdvanceSearch filterByApi={true} rowData={rowData} setFilteredData={setSearchValue}/>
        </div>
      </TabletMobileToolBar>
    </>
  );
}

export default memo(AlertsToolbar);